import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { ChevronDown, ChevronUp, Mail, UserPlus, X, Copy, RefreshCw, Inbox, MailCheck, MailX } from "lucide-react";
import { usePersistedState } from "../hooks/usePersistedState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccessRequestsPanel() {
  const [collapsed, setCollapsed] = usePersistedState("ui.settings.accessRequests.collapsed", false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApproved, setShowApproved] = useState(false);
  const [temp, setTemp] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadCfg = async () => {
    try {
      const r = await axios.get(`${API}/access-request/email-config`);
      setCfg(r.data);
    } catch (_) { /* ignore */ }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/access-request/test-email`);
      if (r.data.sent) {
        alert(`Test email sent to ${r.data.recipient} from ${r.data.sender}.\nResend id: ${r.data.resend_id || 'n/a'}`);
      } else {
        alert(`Test email NOT sent: ${r.data.reason || 'unknown reason'}`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/access-request/list${showApproved ? "?status=all" : ""}`);
      setRows(r.data.data || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadCfg(); }, [showApproved]);

  // Re-fetch the diagnostic strip whenever the SMTP settings panel saves
  // so the "Email delivery: enabled/disabled" banner updates instantly.
  useEffect(() => {
    const handler = () => loadCfg();
    window.addEventListener("smtp-settings-updated", handler);
    return () => window.removeEventListener("smtp-settings-updated", handler);
  }, []);

  const approve = async (row, role = "user") => {
    if (!window.confirm(`Create a ${role} account for ${row.email}?`)) return;
    try {
      const r = await axios.post(`${API}/access-request/${row.id}/approve`, { role });
      if (r.data.temp_password) {
        setTemp({ email: r.data.email, password: r.data.temp_password, role: r.data.role });
      } else {
        alert(r.data.message || "Approved.");
      }
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to approve");
    }
  };

  const dismiss = async (row) => {
    if (!window.confirm(`Dismiss request from ${row.email}?`)) return;
    try {
      await axios.post(`${API}/access-request/${row.id}/dismiss`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to dismiss");
    }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const others = rows.filter((r) => r.status !== "pending");

  return (
    <Card data-testid="access-requests-card">
      <CardHeader className="cursor-pointer" onClick={() => setCollapsed((c) => !c)}>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Inbox className="w-4 h-4" /> Access Requests
            {pending.length > 0 && (
              <span className="ml-1 text-xs bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 px-2 py-0.5 rounded-full font-semibold" data-testid="access-requests-badge">
                {pending.length}
              </span>
            )}
          </span>
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </CardTitle>
      </CardHeader>
      {!collapsed && (
        <CardContent>
          {cfg && (
            <div
              className={`mb-4 rounded-lg border p-3 text-xs ${cfg.will_send ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}
              data-testid="email-diagnostics"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-semibold text-sm">
                  Email delivery: {cfg.will_send
                    ? <span className="text-emerald-600 dark:text-emerald-400">enabled</span>
                    : <span className="text-amber-700 dark:text-amber-400">disabled</span>}
                </span>
                <Button size="sm" variant="outline" onClick={sendTest} disabled={testing || !cfg.will_send} data-testid="send-test-email">
                  {testing ? "Sending…" : "Send test email"}
                </Button>
              </div>
              <ul className="space-y-0.5 text-muted-foreground">
                <li>ENV: <b className="font-mono">{cfg.environment}</b> &middot; SMTP: <b>{cfg.smtp_host ? `${cfg.smtp_host}:${cfg.smtp_port}` : 'NOT SET'}</b></li>
                <li>User: <b>{cfg.smtp_user || '—'}</b> &middot; From: <b>{cfg.sender || '—'}</b></li>
                <li>To: <b>{cfg.recipient}</b></li>
                {cfg.diagnostic && (
                  <li className="mt-1 text-amber-700 dark:text-amber-400">{cfg.diagnostic}</li>
                )}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs flex items-center gap-1.5 cursor-pointer select-none text-muted-foreground">
              <input
                type="checkbox"
                checked={showApproved}
                onChange={(e) => setShowApproved(e.target.checked)}
                data-testid="access-show-all-toggle"
              />
              Show approved &amp; dismissed
            </label>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="access-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : pending.length === 0 && others.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No requests yet.</p>
          ) : (
            <div className="space-y-2" data-testid="access-requests-list">
              {[...pending, ...others].map((row) => (
                <RequestRow key={row.id} row={row} onApprove={approve} onDismiss={dismiss} />
              ))}
            </div>
          )}
        </CardContent>
      )}

      {temp && <TempPasswordModal info={temp} onClose={() => setTemp(null)} />}
    </Card>
  );
}

function RequestRow({ row, onApprove, onDismiss }) {
  const isPending = row.status === "pending";
  const badgeCls = {
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    dismissed: "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400",
  }[row.status] || "bg-slate-100 text-slate-600";
  return (
    <div className="border rounded-lg p-3 bg-muted/20" data-testid={`access-row-${row.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{row.name}</span>
            <a href={`mailto:${row.email}`} className="text-xs text-sky-600 hover:underline">{row.email}</a>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${badgeCls}`}>{row.status}</span>
            <EmailDeliveryBadge row={row} />
          </div>
          <p className="text-xs text-muted-foreground mt-1"><b>Heard about us:</b> {row.source}</p>
          {row.message && <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-wrap">{row.message}</p>}
          <p className="text-[10px] text-muted-foreground mt-1 font-mono">{new Date(row.created_at).toLocaleString()}</p>
        </div>
        {isPending && (
          <div className="flex flex-col gap-1.5 shrink-0">
            <Button size="sm" onClick={() => onApprove(row, "user")} data-testid={`access-approve-${row.id}`}>
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDismiss(row)} data-testid={`access-dismiss-${row.id}`}>
              <X className="w-3.5 h-3.5 mr-1" /> Dismiss
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Small at-a-glance indicator so the superadmin can spot silent SMTP
// failures. Hover reveals the message-id (or reason if not sent).
function EmailDeliveryBadge({ row }) {
  const sent = !!row.email_sent;
  const msgId = row.message_id || "";
  const label = sent ? "email sent" : "not sent";
  const cls = sent
    ? "text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300"
    : "text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300";
  const title = sent
    ? (msgId ? `Delivered · Message-ID: ${msgId}` : "Delivered")
    : "SMTP was not configured or delivery failed. Check the SMTP Settings panel and re-submit or resend.";
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${cls}`}
      title={title}
      data-testid={`access-email-status-${row.id}`}
    >
      {sent
        ? <MailCheck className="w-2.5 h-2.5" />
        : <MailX className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}

function TempPasswordModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(info.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="fixed inset-0 z-[900] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="temp-password-modal">
      <div className="w-full max-w-md rounded-xl bg-background border border-border shadow-2xl">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4 text-emerald-500" /> Account created
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm">
            <b>{info.email}</b> is now a <b>{info.role}</b>. Share these credentials with them
            out-of-band — this password is shown ONCE.
          </p>
          <div className="flex items-center gap-2 rounded-lg bg-muted p-3 font-mono text-sm">
            <span className="flex-1 truncate select-all">{info.password}</span>
            <Button size="sm" variant="outline" onClick={copy} data-testid="copy-temp-password">
              <Copy className="w-3.5 h-3.5 mr-1" /> {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Tell them to change it after they log in for the first time.
          </p>
        </div>
        <div className="px-6 py-3 border-t border-border flex justify-end">
          <Button onClick={onClose} data-testid="temp-password-close">Done</Button>
        </div>
      </div>
    </div>
  );
}
